import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { useState } from "react";
import { toast } from "sonner";

import { ConfirmationModal } from "@/components/ConfirmationModal";
import { EditDialog, type FieldDef, FormPanel } from "@/components/resource-page";
import type { TranslationKey } from "@/constants/i18n";
import { AddButton, ListSection } from "@/components/ui/list-section";
import { Loading } from "@/components/ui/loading";
import { SkeletonLines } from "@/components/ui/skeleton";
import { useRecordParam } from "@/hooks/use-record-param";
import { api, errorMessage, ifMatch } from "@/services/api";
import { listResource } from "@/utils/geo";
import { cn } from "@/utils/utils";
import { useCan } from "@/utils/permissions";
import { useTranslation } from "@/store/locale";

export interface PromoCode {
  id: number;
  code: string;
  valid_until: string;
  description: string | null;
}

const LIST_PAGE = 10;

/** A code, when it stops working, and what it is for. */
const PROMO_FIELDS = (t: (key: TranslationKey) => string): FieldDef[] => [
  { name: "code", label: t("promo.code"), required: true },
  { name: "valid_until", label: t("promo.validUntil"), required: true, type: "date" },
  { name: "description", label: t("promo.description"), type: "textarea" },
];

/** The Promo Codes section as designed: a plain list, Edit / Delete, add; five more per scroll. */
export function PromoCodesSection() {
  const t = useTranslation();
  const allow = useCan();
  const queryClient = useQueryClient();
  const [deleting, setDeleting] = useState<PromoCode | null>(null);
  // Expired codes are noise most days; they stay a click away.
  const [showExpired, setShowExpired] = useState(false);

  // Ten at a time; the next ten when the list is scrolled to its end.
  const codes = useInfiniteQuery({
    queryKey: ["/promo-codes", "infinite", showExpired],
    queryFn: ({ pageParam, signal }) =>
      listResource<PromoCode>(
        "/promo-codes",
        { page: pageParam, size: LIST_PAGE, active_only: !showExpired },
        signal,
      ),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.page < last.pages ? last.page + 1 : undefined),
  });
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["/promo-codes"] });
  const remove = useMutation({
    mutationFn: (row: { id: number; updated_at?: string }) =>
      api.delete(`/promo-codes/${row.id}`, ifMatch(row)),
    onSuccess: () => {
      toast.success(t("promo.deleted"));
      void invalidate();
    },
    onError: (error) => toast.error(errorMessage(error, t("promo.deleteFailed"))),
  });

  const rows = codes.data?.pages.flatMap((page) => page.items) ?? [];
  const { editing, setEditing, loading } = useRecordParam<PromoCode>(
    "edit",
    "promo-code",
    "/promo-codes",
  );

  // A record is read when it is opened, so between the click and the answer
  // there is nothing to fill the form with. The same panel, empty, rather than
  // the list sitting there as though the click had missed.
  if (loading) {
    return (
      <div className="pr-5">
        <FormPanel
          title={t("rp.editSingular").replace("{singular}", t("promo.code"))}
          onClose={() => setEditing(null)}
          actions={null}
        >
          <Loading busy subject={t("promo.code")}>
            <SkeletonLines rows={3} />
          </Loading>
        </FormPanel>
      </div>
    );
  }

  // Adding and editing take over the panel, as they do on every other
  // configuration screen, rather than each list opening a dialog of its own.
  if (editing !== null) {
    return (
      <div className="pr-5">
        <EditDialog
          variant="panel"
          singular={t("promo.code")}
          endpoint="/promo-codes"
          fields={PROMO_FIELDS(t)}
          initial={
            editing === "new"
              ? {}
              : {
                  code: editing.code,
                  valid_until: editing.valid_until,
                  description: editing.description ?? "",
                }
          }
          id={editing === "new" ? null : editing.id}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void invalidate();
          }}
        />
      </div>
    );
  }

  return (
    <div className="pr-5">
      <h2 className="text-title-2 text-heading">{t("promo.title")}</h2>
      <p className="text-fg-muted text-callout mt-1">{t("promo.intro")}</p>
      <ListSection
        className="mt-6"
        title={t("promo.title")}
        shown={rows.length}
        total={codes.data?.pages[0]?.total}
        refreshKeys={[["/promo-codes"]]}
        action={
          <>
            <label className="text-fg-muted text-callout mr-2 flex cursor-pointer items-center gap-2">
              <input
                type="checkbox"
                className="accent-accent size-4"
                checked={showExpired}
                onChange={(event) => setShowExpired(event.target.checked)}
              />
              {t("promo.showExpired")}
            </label>
            {allow("add__promo_code") ? (
              <AddButton onClick={() => setEditing("new")}>{t("promo.add")}</AddButton>
            ) : null}
          </>
        }
        hasMore={codes.hasNextPage}
        loadingMore={codes.isFetchingNextPage}
        pending={codes.isPending}
        onLoadMore={() => void codes.fetchNextPage()}
        empty={t("promo.none")}
      >
        {rows.map((row) => (
          <li
            key={row.id}
            className="grid grid-cols-[minmax(0,1fr)_200px_auto] items-center gap-4 py-2.5"
          >
            <span className="min-w-0">
              <span className="text-fg text-callout block truncate">{row.code}</span>
              <span
                className={cn(
                  "text-subhead block truncate leading-[18px]",
                  row.description ? "text-fg-muted" : "text-fg-subtle italic",
                )}
              >
                {row.description || t("promo.noDescription")}
              </span>
            </span>
            <span className="text-fg-muted text-subhead leading-[18px] whitespace-nowrap">
              {t("promo.validUntil")}: {format(new Date(row.valid_until), "MM/dd/yyyy")}
            </span>
            <span className="text-subhead flex items-center gap-1">
              {allow("change__promo_code") ? (
                <button
                  type="button"
                  onClick={() => setEditing(row)}
                  className="text-fg-muted hover:bg-surface-inset hover:text-fg rounded-lg px-2 py-1 font-medium transition-colors"
                >
                  {t("common.edit")}
                </button>
              ) : null}
              {allow("delete__promo_code") ? (
                <button
                  type="button"
                  onClick={() => setDeleting(row)}
                  className="text-fg-muted hover:bg-danger-soft hover:text-danger rounded-lg px-2 py-1 font-medium transition-colors"
                >
                  {t("common.delete")}
                </button>
              ) : null}
            </span>
          </li>
        ))}
      </ListSection>

      <ConfirmationModal
        isOpen={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={() => {
          if (deleting) remove.mutate(deleting);
          setDeleting(null);
        }}
        title={t("common.confirmDeleteTitle").replace("{model}", t("promo.code"))}
        message={deleting ? t("promo.confirmDelete").replace("{code}", deleting.code) : ""}
      />
    </div>
  );
}
